import React, { useMemo } from "react";
import { cn } from "@/lib/utils";
import { dims, shade } from "./rigGeometry";

const COS = 0.866;
const SIN = 0.5;
const proj = (x, y, z) => [(x - z) * COS, (x + z) * SIN - y];
const path = (pts) => pts.map(([x, y], i) => `${i ? "L" : "M"}${x.toFixed(2)},${y.toFixed(2)}`).join(" ") + " Z";

/**
 * Isometric product thumbnail generated from the module type's real dimensions, colour and window count – the same
 * object the user will see in the scene, so the library reads as a kit of parts rather than a list of rows.
 */
export const IsoThumb = ({ type, size = 40, className, muted = false, title }) => {
  const svg = useMemo(() => {
    const d = dims(type);
    const w = d.w, l = d.l, h = d.h;
    const t0 = proj(0, h, 0), t1 = proj(w, h, 0), t2 = proj(w, h, l), t3 = proj(0, h, l);
    const b1 = proj(w, 0, 0), b2 = proj(w, 0, l), b3 = proj(0, 0, l);
    const pts = [t0, t1, t2, t3, b1, b2, b3];
    const xs = pts.map((p) => p[0]), ys = pts.map((p) => p[1]);
    const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
    const pad = 2;
    const s = (size - pad * 2) / Math.max(maxX - minX, maxY - minY);
    const ox = pad + ((size - pad * 2) - (maxX - minX) * s) / 2 - minX * s;
    const oy = pad + ((size - pad * 2) - (maxY - minY) * s) / 2 - minY * s;
    const T = ([x, y]) => [x * s + ox, y * s + oy];
    const windows = Math.max(0, Math.min(6, Number(type.windows) || 0));
    const win = [];
    if (windows && type.kind !== "trapp") {
      const stories = Math.max(1, Math.round(h / 3));
      const ww = Math.min(1.0, (l / windows) * 0.5);
      for (let i = 0; i < windows; i++) {
        const zc = ((i + 0.5) * l) / windows;
        for (let st = 0; st < stories; st++) {
          const y0 = st * 3 + 1.1, y1 = st * 3 + 2.1;
          if (y1 > h) continue;
          win.push(path([T(proj(w, y0, zc - ww / 2)), T(proj(w, y0, zc + ww / 2)), T(proj(w, y1, zc + ww / 2)), T(proj(w, y1, zc - ww / 2))]));
        }
      }
    }
    const base = type.color || "#d9dee4";
    return {
      top: path([t0, t1, t2, t3].map(T)),
      left: path([b3, b2, t2, t3].map(T)),
      right: path([b1, b2, t2, t1].map(T)),
      win,
      colors: { top: shade(base, 0.26), left: shade(base, -0.04), right: shade(base, -0.18), stroke: shade(base, -0.45) },
    };
  }, [type, size]);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} className={cn("shrink-0", muted && "opacity-60", className)} aria-hidden={!title} role={title ? "img" : undefined}>
      {title && <title>{title}</title>}
      <g stroke={svg.colors.stroke} strokeWidth={0.75} strokeOpacity={0.55} strokeLinejoin="round">
        <path d={svg.left} fill={svg.colors.left} />
        <path d={svg.right} fill={svg.colors.right} />
        <path d={svg.top} fill={svg.colors.top} />
      </g>
      {svg.win.map((d, i) => <path key={i} d={d} fill="#3b4652" fillOpacity={0.7} />)}
    </svg>
  );
};
